import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Loader2, Bot, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlobalChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey! I'm Alex, your local SEO consultant. Are you struggling to get found on Google Maps? I can help figure out what's going wrong — and what it's costing you." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat/general`, {
        question: q,
        session_id: sessionId,
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.answer }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I hit a snag. Try asking again or run your free audit at audit.rankifylocal.com!" }]);
    }
    setLoading(false);
  };

  // Don't show on report pages (has its own chat) or admin pages
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    if (path.startsWith("/report/") || path.startsWith("/admin")) return null;
  }

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="global-chat-trigger"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
          style={{ background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" }}
        >
          <MessageSquare className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="global-chat-panel"
          className="fixed bottom-5 right-5 z-50 flex flex-col overflow-hidden shadow-2xl"
          style={{
            width: 360, maxWidth: "calc(100vw - 40px)",
            height: 520, maxHeight: "calc(100vh - 100px)",
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ background: "#0F172A" }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#6366f1" }}>
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-white leading-tight">Alex — SEO Consultant</p>
                <p className="text-[10px] text-emerald-400">Online now</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white transition" data-testid="global-chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ background: "#f8fafc" }}>
            {messages.map((m, i) => (
              <div key={`${m.role}-${i}`} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "assistant" ? "bg-indigo-100" : "bg-slate-800"}`}>
                  {m.role === "assistant" ? <Bot className="w-3.5 h-3.5 text-indigo-600" /> : <User className="w-3.5 h-3.5 text-white" />}
                </div>
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "assistant"
                    ? "bg-white border border-slate-200 text-slate-700 rounded-tl-sm"
                    : "bg-indigo-600 text-white rounded-tr-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-indigo-600" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t bg-white px-3 py-2.5">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") sendMessage(); }}
                placeholder="Ask about your rankings..."
                className="flex-1 text-sm border-none outline-none bg-transparent text-slate-700 placeholder:text-slate-400"
                data-testid="global-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-40"
                style={{ background: "#6366f1" }}
                data-testid="global-chat-send"
              >
                <Send className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
            <p className="text-[9px] text-slate-400 mt-1 text-center">Powered by RankifyLocal AI</p>
          </div>
        </div>
      )}
    </>
  );
}
